
import { useRef } from 'react';
import StatisticsCards from './StatisticsCards';

interface AppInterfaceProps {
  onButtonClick: (event: React.MouseEvent) => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
}

const AppInterface = ({ onButtonClick, buttonRef }: AppInterfaceProps) => {
  const meditationButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="px-6 pt-6 pb-2 space-y-1 h-full flex flex-col">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl font-black text-purple-900 mb-1">Today's PACTs</h3>
        <p className="text-purple-600 font-semibold text-base">Day 7 of 21</p>
      </div>
      
      {/* Main PACT Card */}
      <div className="bg-white rounded-3xl p-4 shadow-xl border border-purple-100 relative overflow-hidden flex-1 space-y-3">
        {/* Subtle background pattern */}
        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-yellow-400/10 to-purple-400/10 rounded-full blur-xl"></div>
        
        {/* First PACT - Reading */}
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-purple-900 font-black text-lg">📖</span>
            </div>
            <div>
              <h4 className="font-black text-gray-900 text-lg">Read 10 pages</h4>
              <p className="text-gray-600 font-medium text-sm">for 21 days</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex justify-between text-xs font-semibold text-gray-600 mb-2">
              <span>Progress</span>
              <span>7/21 days</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-gradient-to-r from-yellow-400 to-orange-400 h-2 rounded-full w-1/3 shadow-inner"></div>
            </div>
          </div>
          
          {/* Action Button with instruction */}
          <div className="text-center space-y-1 mb-3">
            <button 
              ref={buttonRef}
              onClick={onButtonClick}
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-300 hover:to-yellow-400 text-purple-900 font-black px-8 py-3 rounded-2xl text-base shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden w-full"
            >
              <span className="relative z-10">I Showed Up!</span>
              <div className="absolute inset-0 bg-white/20 rounded-2xl opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
            </button>
            <p className="text-purple-600 font-medium text-sm animate-pulse">
              👆 Click here to celebrate together!
            </p>
          </div>
        </div>

        {/* Second PACT - Meditation */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-3 border border-blue-100 relative">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-500 rounded-full flex items-center justify-center shadow-md">
              <span className="text-white font-black text-sm">🧘‍♂️</span>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 text-base">Meditate 10 minutes</h4>
              <p className="text-gray-600 font-medium text-xs">for 21 days</p>
            </div>
          </div>
          
          {/* Mini Progress Bar */}
          <div className="mb-2">
            <div className="flex justify-between text-xs font-semibold text-gray-600 mb-1">
              <span>Progress</span>
              <span>5/21 days</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div className="bg-gradient-to-r from-blue-400 to-indigo-400 h-1.5 rounded-full w-1/4 shadow-inner"></div>
            </div>
          </div>
          
          {/* Compact Action Button */}
          <button 
            ref={meditationButtonRef}
            onClick={onButtonClick}
            className="bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-300 hover:to-blue-400 text-white font-bold px-6 py-2 rounded-xl text-sm shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 w-full"
          >
            Complete Today
          </button>
        </div>
        
        {/* Animated Statistics with Charts */}
        <StatisticsCards />
        
        {/* Motivational Message - Below statistics */}
        <div className="text-center bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-2 border border-purple-200">
          <p className="text-purple-700 font-bold text-sm leading-tight">
            "You're doing amazing!<br />7 days strong 💪"
          </p>
        </div>
      </div>
    </div>
  );
};

export default AppInterface;
